import React from "react";
import {
  Info,
  ArrowDownToLine,
  ArrowUpFromLine,
  Braces,
  GitBranch,
  Lightbulb,
  Sparkles,
} from "lucide-react";
import { getFlowNodeInfo, SYSTEM_VARS, VAR_NAMING } from "../../utils/flowNodeInfoGuide";

const SectionBlock = ({ icon: Icon, title, children, iconClass = "text-indigo-600", titleClass = "text-indigo-700" }) => (
  <div className="mb-3 last:mb-0">
    <div className={`flex items-center gap-1.5 mb-1.5 text-[10px] font-bold uppercase tracking-wider ${titleClass}`}>
      <Icon size={12} className={iconClass} />
      {title}
    </div>
    <div className="text-[11px] text-gray-700 leading-relaxed space-y-1.5 pl-0.5">{children}</div>
  </div>
);

const NodeInfoPanel = ({ nodeType }) => {
  const info = getFlowNodeInfo(nodeType);

  return (
    <div className="bg-gradient-to-br from-indigo-50/90 to-slate-50 rounded-lg border border-indigo-100 p-3 max-h-[min(52vh,480px)] overflow-y-auto">
      <div className="flex items-start gap-2 mb-3 pb-2 border-b border-indigo-100">
        <div className="p-1.5 bg-indigo-100 rounded-lg shrink-0">
          <Info size={14} className="text-indigo-700" />
        </div>
        <div>
          <p className="text-[12px] font-bold text-indigo-950">{info.title}</p>
          <p className="text-[11px] text-gray-600 mt-0.5">{info.summary}</p>
        </div>
      </div>

      <SectionBlock icon={Sparkles} title="Variable syntax" iconClass="text-violet-600" titleClass="text-violet-800">
        <p>{VAR_NAMING}</p>
      </SectionBlock>

      <SectionBlock icon={ArrowDownToLine} title="Creates (output)" iconClass="text-emerald-600" titleClass="text-emerald-800">
        {info.creates?.length ? (
          <ul className="list-disc pl-4 space-y-1">
            {info.creates.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 italic">Does not create new variables.</p>
        )}
      </SectionBlock>

      <SectionBlock icon={ArrowUpFromLine} title="Uses (input)" iconClass="text-sky-600" titleClass="text-sky-800">
        {info.consumes?.length ? (
          <ul className="list-disc pl-4 space-y-1">
            {info.consumes.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 italic">No variables required as input.</p>
        )}
      </SectionBlock>

      {info.formats?.length > 0 && (
        <SectionBlock icon={Braces} title="Value formats" iconClass="text-amber-600" titleClass="text-amber-800">
          {info.formats.map((f, i) => (
            <div key={i} className="bg-white/80 rounded-md border border-amber-100/80 px-2 py-1.5">
              <p className="font-semibold text-gray-800 text-[10px] mb-0.5">{f.label}</p>
              <pre className="text-[10px] font-mono text-gray-600 whitespace-pre-wrap break-all">{f.example}</pre>
            </div>
          ))}
        </SectionBlock>
      )}

      {info.connections?.length > 0 && (
        <SectionBlock icon={GitBranch} title="Connections" iconClass="text-teal-600" titleClass="text-teal-800">
          <ul className="list-disc pl-4 space-y-1">
            {info.connections.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </SectionBlock>
      )}

      {info.tips?.length > 0 && (
        <SectionBlock icon={Lightbulb} title="Tips" iconClass="text-orange-600" titleClass="text-orange-800">
          <ul className="list-disc pl-4 space-y-1">
            {info.tips.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </SectionBlock>
      )}

      <details className="mt-3 pt-2 border-t border-indigo-100">
        <summary className="text-[10px] font-bold text-gray-500 cursor-pointer hover:text-indigo-700">
          System variables (always available)
        </summary>
        <ul className="mt-2 space-y-1">
          {SYSTEM_VARS.map((v) => (
            <li key={v.name} className="text-[10px]">
              <code className="font-mono text-indigo-700 bg-indigo-50 px-1 rounded">{`{{${v.name}}}`}</code>
              <span className="text-gray-500"> — {v.desc}</span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
};

export default NodeInfoPanel;
